/**
 * DashboardTopBar - Top bar za Dashboard layout
 * 
 * Prikazuje greeting, korisničko ime i theme toggle.
 * 
 * @example
 * ```tsx
 * <DashboardTopBar onMenuClick={handleMenuClick} />
 * ```
 */

"use client";

import {
  Badge,
  Box,
  Flex,
  Heading,
  IconButton,
  Spinner,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { useEffect, useMemo, useRef } from "react";
import { usePathname } from "next/navigation";
import { HiOutlineBars3 } from "react-icons/hi2";
import { useAuth } from "@/lib/contexts";
import { DASHBOARD_TOPBAR_STICKY_HEIGHT_VAR } from "@/lib/layout/dashboard-sticky-top";
import { ThemeToggle, NotificationButton, RoleSwitcher } from "../ui";
import { useTuitionCalculationActivity } from "@/features/tuition/hooks/useTuitionCalculationActivity";
import { useThemeTokens } from "@/lib/theme/tokens";
import { useClassicDashboardStyles } from "@/lib/hooks/useClassicDashboardStyles";
import { Card } from "@/components/ui";
import { useStudioFeatureSettings } from "@/features/dashboard/hooks/useStudioFeatureSettings";
import {
  getRoleColorScheme,
  getSwitchableAdminRoles,
  resolveVisibleActiveRole,
} from "@/lib/utils/roles";
import { useStudioClock } from "@/hooks/shared/useStudioClock";
import { studioGreeting } from "@/lib/utils/date/studio-clock";

interface DashboardTopBarProps {
  onMenuClick: () => void;
}

export const DashboardTopBar = ({ onMenuClick }: DashboardTopBarProps) => {
  const topBarRef = useRef<HTMLDivElement>(null);
  const user = useAuth();
  const pathname = usePathname();
  const name = user?.firstName || user?.name || "Member";
  const theme = useThemeTokens();
  const { isClassic, controlBorderRadius } = useClassicDashboardStyles();

  const { choreographerPortalEnabled, isPending } = useStudioFeatureSettings();
  const portalKnown = !isPending;
  const visibleRole = resolveVisibleActiveRole(
    user,
    choreographerPortalEnabled,
    portalKnown,
  );
  const switchableRoles = getSwitchableAdminRoles(user, {
    choreographerPortalEnabled,
    choreographerPortalKnown: portalKnown,
  });
  const isAdminPage = pathname?.startsWith("/admin") ?? false;
  const isForbiddenPage = pathname === "/forbidden";
  const showRoleSwitcher =
    (isAdminPage || isForbiddenPage) && switchableRoles.length > 1;

  const { isRunning: isTuitionCalculating } = useTuitionCalculationActivity();
  const studioClock = useStudioClock();

  const greeting = useMemo(
    () => studioGreeting(studioClock.hour),
    [studioClock.hour],
  );

  const activeRole = visibleRole;
  const subtitle = useMemo(() => {
    if (!isAdminPage) {
      return "Manage your students' classes, payments, and enrollment.";
    }
    switch (activeRole ?? "") {
      case "Admin":
        return "Manage classes, students, teachers, and appointments.";
      case "Teacher":
        return "View your classes, private lessons, and hours.";
      case "Staff":
        return "Support parents, check private lessons, and manage your time.";
      case "Choreographer":
        return "Your dances, contract, and deliveries.";
      default:
        return "Manage classes, students, teachers, and appointments.";
    }
  }, [isAdminPage, activeRole]);

  const roleBadge = useMemo(() => {
    if (!pathname?.startsWith("/admin") || !activeRole) return null;
    const role = activeRole;

    const colorScheme = getRoleColorScheme(role);
    return (
      <Badge
        colorScheme={colorScheme}
        fontSize={{ base: "xs", md: "sm" }}
        px={{ base: 2, md: 3 }}
        py={1}
        borderRadius="full"
        textTransform="uppercase"
        letterSpacing="0.1em"
        fontWeight="600"
        boxShadow="sm"
      >
        {role}
      </Badge>
    );
  }, [pathname, activeRole]);

  useEffect(() => {
    const topBarEl = topBarRef.current;
    const scrollContainer = topBarEl?.closest(
      "[data-scroll-container]",
    ) as HTMLElement | null;
    if (!topBarEl || !scrollContainer) return;

    const syncHeight = () => {
      scrollContainer.style.setProperty(
        DASHBOARD_TOPBAR_STICKY_HEIGHT_VAR,
        `${topBarEl.getBoundingClientRect().height}px`,
      );
    };

    syncHeight();
    const observer = new ResizeObserver(syncHeight);
    observer.observe(topBarEl);
    window.addEventListener("resize", syncHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", syncHeight);
    };
  }, [greeting, name, showRoleSwitcher, subtitle]);

  return (
    <Box
      ref={topBarRef}
      data-dashboard-topbar
      px={{ base: 4, lg: 8 }}
      pt={{ base: 6, lg: 8 }}
      pb={{ base: 2, lg: 2 }}
      mb={4}
      position="sticky"
      top={0}
      zIndex={20}
    >
      <Card
        p={4}
        borderColor={theme.borderGlass.default}
        borderWidth="1px"
        bg={isClassic ? theme.surfaceGlass.sidebar : "none"}
      >
        <Flex
          align={{ base: "flex-start", md: "center" }}
          justify="space-between"
          gap={4}
        >
          <Box flex="1" minW={0}>
            <Flex 
              align={{ base: "flex-start", md: "center" }}
              direction={{ base: "column-reverse", md: "row" }}
              mb={1}
              gap={{ base: 2, md: 0 }}
            >
              <Heading
                as="h1"
                fontSize={{ base: "2xl", md: "3xl" }}
                letterSpacing="-0.05em"
                color={theme.text.primary}
                fontWeight="600"
                lineHeight="1.2"
                noOfLines={2}
                overflow="unset"
              >
                {greeting}, {name}
              </Heading>
              {roleBadge && (
                <Box
                  ml={{ base: 0, md: 3 }}
                  mt={{ base: 0, md: 0 }}
                  mb={{ base: 0, md: 0 }}
                  flexShrink={0}
                >
                  {roleBadge}
                </Box>
              )}
            </Flex>
            <Text 
              fontSize="sm" 
              color={theme.text.tertiary} 
              mt={{ base: 1, md: 0 }}
              display={{ base: "none", md: "block" }}
            >
              {subtitle}
            </Text>
          </Box>

          <Flex align={{ base: "flex-start", md: "center" }} gap={2}>
            {isTuitionCalculating ? (
              <Tooltip label="Calculating tuition" placement="bottom">
                <Flex
                  align="center"
                  justify="center"
                  w="32px"
                  h="32px"
                  borderRadius="8px"
                  borderWidth="1px"
                  borderColor={theme.borderGlass.default}
                  bg={theme.accent.mutedBg}
                  color={theme.accent.muted}
                  data-testid="tuition-header-calculating-spinner"
                >
                  <Spinner size="sm" color={theme.accent.muted} thickness="2.5px" />
                </Flex>
              </Tooltip>
            ) : null}

            {/* Notification Button */}
            <NotificationButton size="sm" />

            {showRoleSwitcher && <RoleSwitcher size="sm" />}

            {/* Theme Toggle (desktop; mobile uses sidebar drawer) */}
            <Box display={{ base: "none", md: "flex" }}>
              <ThemeToggle size="sm" />
            </Box>

            {/* Mobile Menu Button */}
            <Box display={{ base: "flex", md: "none" }}>
              <IconButton
                aria-label="Open menu"
                icon={<HiOutlineBars3 />}
                variant="ghost"
                onClick={onMenuClick}
                color={theme.text.secondary}
                borderWidth="1px"
                borderColor={theme.borderGlass.default}
                borderLeftWidth="1px"
                borderLeftColor={theme.borderGlass.default}
                borderRadius={controlBorderRadius}
                sx={{
                  WebkitTapHighlightColor: "transparent",
                  "@media (hover: hover)": {
                    "&:hover": {
                      bg: theme.surfaceGlass.cardStrong,
                      borderColor: theme.accent.primaryBorder,
                      color: theme.accent.primary,
                    },
                  },
                  "@media (hover: none) and (pointer: coarse)": {
                    "&:hover, &:active, &:focus, &:focus-visible, &[data-active]": {
                      bg: "transparent",
                      borderColor: theme.borderGlass.default,
                      color: theme.text.secondary,
                      boxShadow: "none",
                      outline: "none",
                    },
                  },
                }}
              />
            </Box>
          </Flex>
        </Flex>
      </Card>
    </Box>
  );
};

